/**
 * Transmit and the transmission log (mission 7 WP07 T039; FR-008, FR-009,
 * FR-012, FR-013, NFR-008; TX.4, TX.6, TX.8, X.10, GEN.4, GEN.6;
 * contracts/transmission-api.md; analysis I1).
 *
 * - `transmit` builds its body key by key (C-006). The acknowledgment (TX.4)
 *   travels only when the caller gives one; at 0 ERROR it is absent.
 * - `listTransmissions` validates every filter before any fetch: the unions with
 *   `oneOf`, the run as a UUID, the dates as `YYYY-MM-DD`, and `q` at 1 to 64
 *   characters. `q` is sent only as a query parameter of this read, never in a
 *   path, a body, a query key's URL or a log.
 * - Nothing here is charged or touches credits (TX.8, C-005).
 */
import { InvalidIdError, api, idSegment, oneOf } from '../api'
import { BUREAUS, type Bureau } from './headerParams'
import type { RunState } from './runs'

export type TransmissionResult = 'pending' | 'success' | 'auth_failed' | 'timeout' | 'not_sent'
export const TRANSMISSION_RESULTS: readonly TransmissionResult[] = ['pending', 'success', 'auth_failed', 'timeout', 'not_sent']

/** `transmission.Detail`: why an attempt ended as it did. */
export type TransmissionDetail =
  | 'auth-rejected'
  | 'host-timeout'
  | 'interrupted'
  | 'mgts-unavailable'
  | 'credential-changed'
  | 'file-unavailable'
  | 'bureau-disabled'
  | 'host-refused'
  | 'not-attempted'
export const TRANSMISSION_DETAILS: readonly TransmissionDetail[] = [
  'auth-rejected',
  'host-timeout',
  'interrupted',
  'mgts-unavailable',
  'credential-changed',
  'file-unavailable',
  'bureau-disabled',
  'host-refused',
  'not-attempted',
]

/** One attempt: one file, one bureau, one SFTP session at most (TX.6). */
export interface TransmissionEntry {
  transmission_id: string
  run_id: string
  bureau: Bureau
  result: TransmissionResult
  detail: TransmissionDetail | null
  bytes_sent: number
  remote_filename: string
  /** The acknowledged ERROR count, or null at 0 ERROR. */
  error_ack_count: number | null
  attempted_at: string
  completed_at: string | null
  /** The tenant's contact email (one identity; A.5 as amended). */
  acting_email: string
}

/** Why a run can never be transmitted (`run.TransmitBlocked()`); the page renders the matching sentence. */
export type TransmitIneligibleReason = 'X.10' | 'GEN.4' | 'GEN.6'

/** GET /v1/transmissions. */
export interface TransmissionsPage {
  entries: TransmissionEntry[]
  next_cursor: string | null
}

/** GET /v1/runs/{id}/transmissions: the page fixed to the run, plus the not-sent line. */
export interface RunTransmissions extends TransmissionsPage {
  run_id: string
  transmit_eligible: boolean
  transmit_ineligible_reason: TransmitIneligibleReason | null
  /** Only with `GEN.4`. */
  run_state?: RunState
  /** Absent when the run is not transmit-eligible. */
  remote_filename?: string
  /** Bureaus with no `success` attempt of this run; `[]` when not eligible. */
  not_sent_to: Bureau[]
}

/** POST /v1/runs/{id}/transmit. Both acknowledgment members, or neither (TX.4, squad S7). */
export interface TransmitBody {
  bureaus: Bureau[]
  acknowledged_error_count?: number
  ack_wording_version?: string
}

export interface TransmitAttempt {
  transmission_id: string
  bureau: Bureau
  result: TransmissionResult
  remote_filename: string
  attempted_at: string
}

/** The 202. `acknowledgment` is null at 0 ERROR. */
export interface TransmitAccepted {
  run_id: string
  acknowledgment: { error_count: number; ack_wording_version: string; recorded_at: string } | null
  attempts: TransmitAttempt[]
}

/** The log's filters, as the URL search params hold them (sheet 32). */
export interface TransmissionFilters {
  run?: string
  bureau?: Bureau
  result?: TransmissionResult
  /** `YYYY-MM-DD`, inclusive, UTC. */
  from?: string
  to?: string
  /** 1 to 64 characters; an empty string is no filter. */
  q?: string
}

/** The log's page size (NFR-005). */
const PAGE = '50'
const DAY = /^\d{4}-\d{2}-\d{2}$/
const Q_MAX = 64

function day(value: string, param: 'from' | 'to'): string {
  if (!DAY.test(value)) throw new InvalidIdError(param)
  return value
}

export async function transmit(runId: string, body: TransmitBody): Promise<TransmitAccepted> {
  const path = `/runs/${idSegment(runId, 'run id')}/transmit`
  const clean: TransmitBody = { bureaus: body.bureaus.map((b) => oneOf(b, BUREAUS, 'bureau')) }
  if (body.acknowledged_error_count !== undefined) clean.acknowledged_error_count = body.acknowledged_error_count
  if (body.ack_wording_version !== undefined) clean.ack_wording_version = body.ack_wording_version
  return api<TransmitAccepted>(path, { method: 'POST', body: JSON.stringify(clean) })
}

export async function getRunTransmissions(runId: string, cursor?: string): Promise<RunTransmissions> {
  const path = `/runs/${idSegment(runId, 'run id')}/transmissions`
  return api<RunTransmissions>(cursor === undefined ? path : `${path}?${new URLSearchParams({ cursor }).toString()}`)
}

export async function listTransmissions(filters: TransmissionFilters, cursor?: string): Promise<TransmissionsPage> {
  const q = new URLSearchParams({ limit: PAGE })
  if (filters.run !== undefined) {
    idSegment(filters.run, 'run id')
    q.set('run', filters.run)
  }
  if (filters.bureau !== undefined) q.set('bureau', oneOf(filters.bureau, BUREAUS, 'bureau'))
  if (filters.result !== undefined) q.set('result', oneOf(filters.result, TRANSMISSION_RESULTS, 'result'))
  if (filters.from !== undefined) q.set('from', day(filters.from, 'from'))
  if (filters.to !== undefined) q.set('to', day(filters.to, 'to'))
  if (filters.q !== undefined && filters.q !== '') {
    if (filters.q.length > Q_MAX) throw new InvalidIdError('q')
    q.set('q', filters.q)
  }
  if (cursor !== undefined) q.set('cursor', cursor)
  return api<TransmissionsPage>(`/transmissions?${q.toString()}`)
}
